import Vue from 'vue';
import CreateAppSystemWebApiRequestModel from '../../../../services/v2/model/create-app-system-web-api-request-model';
import AppSystemResourceViewModel from '../../../../services/v1/viewModel/resource/AppSystemResourceViewModel';
import ViewModelBase from '../../../../core/v2/app/viewModelBase';
import DataSource from '../../../../core/v2/data/data-source';
import DataRecord from '../../../../core/v2/data/data-record';
import AppSystemWebApiModel from '../../../../services/v2/model/app-system-web-api-model';
import ServiceResponse from '../../../../plugins/servicelayer/responseTypes/serviceResponse';

export default class AppSystemWebApiViewModel extends ViewModelBase {
  readonly hiddenPassword = '••••••••';

  appSystemId?: number;
  appSystem = new AppSystemResourceViewModel() as any;
  visibleDialog?: 'create' | 'edit' = undefined;
  appSystemWebApi?: AppSystemWebApiModel = undefined;
  webApiConnectionSuccessful?: boolean;
  webApiConnectionResultMessage: { title?: string, detail?: string } = {};

  async initialize(): Promise<void> {
    this.getAppSystemIdFromUrl();
    this.appSystemWebApiDataSource.filter.appSystemId = this.appSystemId;
    this.appSystemDataSource.filter.id = this.appSystemId;
    await this.appSystemWebApiDataSource.select();
    this.appSystem = await this.appSystemDataSource.select();
  }

  appSystemWebApiDataSource = new DataSource({
    selectCommand: Vue.$service.v2.api.core.appSystemWebApis.getAppSystemWebApiList,
    updateCommand: Vue.$service.v2.api.core.appSystemWebApis.updateAppSystemWebApi,
    insertCommand: Vue.$service.v2.api.core.appSystemWebApis.createAppSystemWebApi,
    deleteCommand: Vue.$service.v2.api.core.appSystemWebApis.deleteAppSystemWebApi,
  });

  appSystemDataSource = new DataSource({
    selectCommand: Vue.$service.v2.api.core.appSystems.getAppSystemById,
  });

  async onDeleteClick(entry: DataRecord<AppSystemWebApiModel>) {
    Vue.$dialog.confirmDelete(1)
      .then(async (isConfirmed) => {
        if (isConfirmed) {
          await this.appSystemWebApiDataSource.delete(entry.data.id!);
          await this.appSystemWebApiDataSource.select();
        }
      });
  }

  showCreateDialog() {
    this.appSystemWebApi = new CreateAppSystemWebApiRequestModel();
    this.appSystemWebApi.appSystemId = this.appSystemId;
    this.webApiConnectionSuccessful = undefined;
    this.webApiConnectionResultMessage = {};
    this.visibleDialog = 'create';
  }

  showEditDialog(editedAppSystemWebApi: AppSystemWebApiModel) {
    this.appSystemWebApi = AppSystemWebApiModel.toModel(AppSystemWebApiModel.toDTO(editedAppSystemWebApi));
    this.hidePasswords(this.appSystemWebApi);
    this.webApiConnectionSuccessful = undefined;
    this.webApiConnectionResultMessage = {};
    this.visibleDialog = 'edit';
  }

  closeDialog() {
    this.visibleDialog = undefined;
    this.appSystemWebApi = undefined;
    this.webApiConnectionSuccessful = undefined;
    this.webApiConnectionResultMessage = {};
  }

  async createOrEditAppSystemWebApi() {
    if (!this.appSystemWebApi) {
      return;
    }

    this.resolvePasswords(this.appSystemWebApi);

    if (this.visibleDialog === 'create') {
      await this.appSystemWebApiDataSource.insert(this.appSystemWebApi);
    } else if (this.visibleDialog === 'edit') {
      await this.appSystemWebApiDataSource.update(this.appSystemWebApi);
    }
    this.closeDialog();
    await this.appSystemWebApiDataSource.select();
  }

  async checkWebApiConnection() {
    if (!this.appSystemWebApi) {
      return;
    }
    try {
      // These properties are required, but the webApi might not have been saved yet
      this.appSystemWebApi.id ??= 0;
      this.appSystemWebApi.creatorId ??= 0;

      const resp = await Vue.$service.v2.api.core.appSystemWebApis.checkWebApiConnection(this.appSystemWebApi);
      this.webApiConnectionSuccessful = !!resp.data;
      this.webApiConnectionResultMessage = { title: this.webApiConnectionSuccessful ? Vue.$t('core.page.appSystemWebApi.checkConnection.success') : Vue.$t('core.page.appSystemWebApi.checkConnection.failed') };
    } catch (e: any | ServiceResponse) {
      this.webApiConnectionSuccessful = false;
      this.webApiConnectionResultMessage = { title: Vue.$t('core.page.appSystemWebApi.checkConnection.failed'), detail: e.detailMessage ?? e.message };
    }
  }

  getAppSystemIdFromUrl() {
    const appSysId = Vue.$router.currentRoute.params.appSystemId;
    this.appSystemId = parseInt(appSysId, 10);
  }

  hidePasswords(appSystemWebApi: AppSystemWebApiModel) {
    appSystemWebApi.apiPassword = appSystemWebApi.hasEncryptedApiPassword ? this.hiddenPassword : undefined;
    appSystemWebApi.proxyPassword = appSystemWebApi.hasEncryptedProxyPassword ? this.hiddenPassword : undefined;
    appSystemWebApi.secret = appSystemWebApi.hasEncryptedSecret ? this.hiddenPassword : undefined;
  }

  resolvePasswords(appSystemWebApi: AppSystemWebApiModel) {
    if (appSystemWebApi.apiPassword === this.hiddenPassword) {
      appSystemWebApi.apiPassword = undefined;
      appSystemWebApi.hasEncryptedApiPassword = true;
    } else {
      appSystemWebApi.hasEncryptedApiPassword = false;
    }
    if (appSystemWebApi.proxyPassword === this.hiddenPassword) {
      appSystemWebApi.proxyPassword = undefined;
      appSystemWebApi.hasEncryptedProxyPassword = true;
    } else {
      appSystemWebApi.hasEncryptedProxyPassword = false;
    }
    if (appSystemWebApi.secret === this.hiddenPassword) {
      appSystemWebApi.secret = undefined;
      appSystemWebApi.hasEncryptedSecret = true;
    } else {
      appSystemWebApi.hasEncryptedSecret = false;
    }
  }
}
